import { useCallback } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { BASE_URL } from "../utils/reportsHelpers";

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/** PDF/Excel via the backend — one source of truth for both the full-report
 * export (ExportMenu) and the DataTable's "Export Selected" (already-fetched
 * row subset). Both go through POST /reports/export-rows -> the same
 * PDFKit/ExcelJS renderers the full export uses, so Arabic text renders
 * correctly (proper font registration / real UTF-8 encoding) instead of the
 * mojibake a client-side jsPDF table or a plain CSV blob produced — jsPDF
 * ships no Arabic-capable font, and a BOM-less CSV blob gets read back by
 * Excel in the system codepage instead of UTF-8. */
const useReportExport = (currentUser) => {
  const downloadReport = useCallback(async (type, format, filters = {}) => {
    const res = await apiFetch(`${BASE_URL}/reports/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, format, filters: { ...filters, requestingUser: currentUser } }),
    });
    if (!res.ok) throw new Error(`Failed to generate ${type} report`);
    const blob = await res.blob();
    const ext = format === "xlsx" ? "xlsx" : "pdf";
    triggerBlobDownload(blob, `${type}-report-${new Date().toISOString().split("T")[0]}.${ext}`);
  }, [currentUser]);

  const exportRows = useCallback(async (rows, format, filename, title) => {
    if (!rows.length) return;
    const res = await apiFetch(`${BASE_URL}/reports/export-rows`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rows, format, title }),
    });
    if (!res.ok) throw new Error(`Failed to export selected rows as ${format}`);
    const blob = await res.blob();
    triggerBlobDownload(blob, `${filename}.${format === "pdf" ? "pdf" : "xlsx"}`);
  }, []);

  const exportRowsToCsv = useCallback((rows, filename) => exportRows(rows, "xlsx", filename), [exportRows]);
  const exportRowsToPdf = useCallback((rows, filename, title) => exportRows(rows, "pdf", filename, title), [exportRows]);

  return { downloadReport, exportRowsToCsv, exportRowsToPdf };
};

export default useReportExport;
